import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Auth.css';

// Simple line chart using SVG (no external dependencies)
type LineChartProps = {
  data: { value: number; time: number }[];
  width?: number;
  height?: number;
};
const LineChart: React.FC<LineChartProps> = ({ data, width = 300, height = 120 }) => {
  // Responsive width: use 100% of parent up to 900px
  const responsiveWidth = Math.min(window.innerWidth - 32, 900);
  if (!data.length) return <svg width={responsiveWidth} height={height} style={{ maxWidth: '100%', display: 'block' }} />;
  const maxY = Math.max(...data.map((d: { value: number }) => d.value), 200);
  const minY = Math.min(...data.map((d: { value: number }) => d.value), 40);
  const points = data.map((d: { value: number }, i: number) => {
    const x = (i / (data.length - 1)) * (responsiveWidth - 40) + 20;
    const y = height - 20 - ((d.value - minY) / (maxY - minY)) * (height - 40);
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={responsiveWidth} height={height} style={{ maxWidth: '100%', display: 'block' }}>
      <polyline
        fill="none"
        stroke="#ff1744"
        strokeWidth="2"
        points={points}
      />
      <g>
        <text x="5" y="15" fontSize="12" fill="#888">{maxY} bpm</text>
        <text x="5" y={height - 5} fontSize="12" fill="#888">{minY} bpm</text>
      </g>
    </svg>
  );
};

const HRM: React.FC = () => {
  const navigate = useNavigate();
  const [connected, setConnected] = useState(false);
  const [heartRate, setHeartRate] = useState<number | null>(null);
  const [data, setData] = useState<{ value: number, time: number }[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [connecting, setConnecting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [selectedSession, setSelectedSession] = useState<any | null>(null);
  // Use 'any' for BluetoothDevice for compatibility
  const deviceRef = useRef<any>(null);
  const intervalRef = useRef<any>(null);

  // Load saved sessions from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('hrm_sessions');
    if (saved) setSessions(JSON.parse(saved));
  }, []);


  // Save session to localStorage
  const saveSession = (sessionData?: { value: number, time: number }[]) => {
    const session = { timestamp: Date.now(), data: sessionData || data };
    const updated = [session, ...sessions];
    setSessions(updated);
    localStorage.setItem('hrm_sessions', JSON.stringify(updated));
  };

  // Stop recording and save session
  const stopAndSave = () => {
    setRecording(false);
    setConnected(false);
    if (deviceRef.current && deviceRef.current.gatt && deviceRef.current.gatt.connected) {
      deviceRef.current.gatt.disconnect();
    }
    if (data.length) {
      saveSession(data);
      setData([]);
    }
  };

  // Connect to HRM device
  const connectHRM = async () => {
    setConnecting(true);
    try {
      // Use 'any' for navigator.bluetooth for compatibility
      const nav: any = navigator;
      const device = await nav.bluetooth.requestDevice({
        filters: [{ services: ['heart_rate'] }],
        optionalServices: ['battery_service']
      });
      deviceRef.current = device;
      const server = await device.gatt.connect();
      const service = await server.getPrimaryService('heart_rate');
      const characteristic = await service.getCharacteristic('heart_rate_measurement');
      characteristic.startNotifications();
      characteristic.addEventListener('characteristicvaluechanged', handleHRMData);
      setConnected(true);
      setRecording(true);
      device.addEventListener('gattserverdisconnected', () => {
        setConnected(false);
        setRecording(false);
      });
    } catch (err) {
      alert('Failed to connect: ' + (err as any).message);
    }
    setConnecting(false);
  };

  // Handle incoming HRM data
  const handleHRMData = (event: any) => {
    const value = event.target.value;
    // Parse heart rate value according to BLE spec
    let hr: number;
    if (value.getUint8(0) & 0x01) {
      hr = value.getUint16(1, true);
    } else {
      hr = value.getUint8(1);
    }
    setHeartRate(hr);
    setData(prev => [...prev, { value: hr, time: Date.now() }]);
  };

  // Update graph every second
  useEffect(() => {
    if (connected && recording) {
      intervalRef.current = setInterval(() => {
        setData(prev => prev.length && heartRate ? [...prev, { value: heartRate, time: Date.now() }] : prev);
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [connected, heartRate, recording]);

  // Session stats helper
  const getSessionStats = (session: any) => {
    if (!session || !session.data || !session.data.length) return null;
    const values = session.data.map((d: any) => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const duration = (session.data[session.data.length - 1].time - session.data[0].time) / 1000;
    return { min, max, duration };
  };

  return (
    <div
      className="hrm-card"
      style={{
        maxWidth: '100vw',
        width: '100%',
        margin: '2rem auto',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        boxSizing: 'border-box',
        padding: '1rem',
      }}
    >
      <button
        onClick={() => navigate(-1)}
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          background: 'transparent',
          border: 'none',
          fontSize: 24,
          cursor: 'pointer',
          color: '#888',
        }}
        aria-label="Close"
      >
        ×
      </button>
      <h2>Heart Rate Monitor</h2>
      {selectedSession ? (
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '60vh',
            position: 'relative',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <div style={{ margin: '1rem 0', width: '100%', display: 'flex', justifyContent: 'center' }}>
            <LineChart data={selectedSession.data} />
          </div>
          <div style={{ marginBottom: 4 }}>Session length: {getSessionStats(selectedSession)?.duration?.toFixed(1) || '--'} sec</div>
          <div style={{ marginBottom: 4 }}>Max HR: {getSessionStats(selectedSession)?.max || '--'} bpm</div>
          <div style={{ marginBottom: 16 }}>Min HR: {getSessionStats(selectedSession)?.min || '--'} bpm</div>
          <h3 style={{ marginTop: 0 }}>Saved Sessions</h3>
          <ul style={{ maxHeight: 100, overflow: 'auto', fontSize: 12, cursor: 'pointer', marginBottom: 0 }}>
            {sessions.map((s, i) => (
              <li
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: selectedSession === s ? '#ffe0b2' : '#f5f5f5',
                  borderRadius: 8,
                  marginBottom: 6,
                  padding: '8px 12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  cursor: 'pointer',
                  border: selectedSession === s ? '1px solid #ff9800' : '1px solid #e0e0e0',
                  fontWeight: selectedSession === s ? 600 : 400,
                  transition: 'background 0.2s, border 0.2s',
                }}
                onClick={e => {
                  // Prevent click if delete icon is clicked
                  if ((e.target as HTMLElement).closest('.delete-session-btn')) return;
                  setSelectedSession(s);
                }}
              >
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span style={{ color: '#333' }}>{new Date(s.timestamp).toLocaleString()}</span>
                  <span style={{ color: '#888', marginLeft: 8 }}>({s.data.length} points)</span>
                </span>
                <button
                  className="delete-session-btn"
                  title="Delete session"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ff1744',
                    fontSize: 18,
                    marginLeft: 10,
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    setSessions(prev => {
                      const updated = prev.filter((_, idx) => idx !== i);
                      localStorage.setItem('hrm_sessions', JSON.stringify(updated));
                      return updated;
                    });
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="5" y="8" width="10" height="7" rx="2" fill="#ff1744" fillOpacity="0.12"/>
                    <rect x="8.5" y="10" width="1" height="3" rx="0.5" fill="#ff1744"/>
                    <rect x="10.5" y="10" width="1" height="3" rx="0.5" fill="#ff1744"/>
                    <rect x="7" y="5" width="6" height="2" rx="1" fill="#ff1744"/>
                    <rect x="4" y="7" width="12" height="1" rx="0.5" fill="#ff1744"/>
                  </svg>
                </button>
              </li>
            ))}
          </ul>
          <button
            className="auth-button"
            onClick={() => setSelectedSession(null)}
            style={{
              position: 'fixed',
              left: 8,
              right: 8,
              bottom: 8,
              width: 'calc(100vw - 16px)',
              margin: 0,
              borderRadius: 0,
              zIndex: 1000,
              textAlign: 'center',
              display: 'block',
            }}
          >
            New session
          </button>
        </div>
      ) : (
        <>
          <div>Status: {connected ? 'Connected' : 'Not Connected'}</div>
          <div style={{ height: 56 }} />
          {/* Stop & Save button now merged with New Session button below */}
          <div style={{ margin: '1rem 0' }}>
            <LineChart data={data} />
          </div>
          <div>Current Heart Rate: {heartRate ? `${heartRate} bpm` : '--'}</div>
          {recording && data.length > 0 && (
            <div style={{ marginTop: 4, fontSize: 14, textAlign: 'left', width: '100%', maxWidth: 400 }}>
              <div>Max HR: {Math.max(...data.map(d => d.value))} bpm</div>
              <div>Min HR: {Math.min(...data.map(d => d.value))} bpm</div>
            </div>
          )}
        </>
      )}
      {/* New Session button fixed at the bottom when not viewing a session */}
      {!selectedSession && (
        <button
          className="auth-button"
          onClick={recording ? stopAndSave : connectHRM}
          disabled={connecting}
          style={{
            position: 'fixed',
            left: 8,
            right: 8,
            bottom: 8,
            width: 'calc(100vw - 16px)',
            margin: 0,
            borderRadius: 0,
            zIndex: 1000,
            textAlign: 'center',
            display: 'block',
            background: recording ? '#ff1744' : undefined,
            color: recording ? '#fff' : undefined,
            fontWeight: recording ? 600 : undefined,
            boxShadow: recording ? '0 2px 8px rgba(255,23,68,0.15)' : undefined,
            transition: 'background 0.2s, color 0.2s',
          }}
        >
          {connecting
            ? 'Connecting...'
            : recording
              ? 'Stop & Save Session'
              : 'New Session'}
        </button>
      )}
      {!selectedSession && (
        <div style={{ width: '100%', maxWidth: 400, alignSelf: 'flex-start', marginTop: 24 }}>
          <h3 style={{ marginTop: 0, textAlign: 'left' }}>Saved Sessions</h3>
          <ul style={{ maxHeight: 100, overflow: 'auto', fontSize: 12, cursor: 'pointer', marginBottom: 0, textAlign: 'left', paddingLeft: 16 }}>
            {sessions.map((s, i) => (
              <li
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: selectedSession === s ? '#ffe0b2' : '#f5f5f5',
                  borderRadius: 8,
                  marginBottom: 6,
                  padding: '8px 12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  cursor: 'pointer',
                  border: selectedSession === s ? '1px solid #ff9800' : '1px solid #e0e0e0',
                  fontWeight: selectedSession === s ? 600 : 400,
                  transition: 'background 0.2s, border 0.2s',
                }}
                onClick={e => {
                  if ((e.target as HTMLElement).closest('.delete-session-btn')) return;
                  setSelectedSession(s);
                }}
              >
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span style={{ color: '#333' }}>{new Date(s.timestamp).toLocaleString()}</span>
                  <span style={{ color: '#888', marginLeft: 8 }}>({s.data.length} points)</span>
                </span>
                <button
                  className="delete-session-btn"
                  title="Delete session"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ff1744',
                    fontSize: 18,
                    marginLeft: 10,
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    setSessions(prev => {
                      const updated = prev.filter((_, idx) => idx !== i);
                      localStorage.setItem('hrm_sessions', JSON.stringify(updated));
                      return updated;
                    });
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="5" y="8" width="10" height="7" rx="2" fill="#ff1744" fillOpacity="0.12"/>
                    <rect x="8.5" y="10" width="1" height="3" rx="0.5" fill="#ff1744"/>
                    <rect x="10.5" y="10" width="1" height="3" rx="0.5" fill="#ff1744"/>
                    <rect x="7" y="5" width="6" height="2" rx="1" fill="#ff1744"/>
                    <rect x="4" y="7" width="12" height="1" rx="0.5" fill="#ff1744"/>
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div style={{ fontSize: 12, color: '#888', marginTop: 10 }}>
        Your HRM data is only saved locally on your device. Uninstalling the app will delete the data.{' '}
        {selectedSession && (
          <>
            Click{' '}
            <span
              style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => {
                // Prepare CSV content
                const csvRows = [
                  'timestamp,value',
                  ...selectedSession.data.map((d: any) => `${new Date(d.time).toISOString()},${d.value}`)
                ];
                const csvContent = csvRows.join('\n');
                // Create blob and download
                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `hrm-session-${selectedSession.timestamp}.csv`;
                document.body.appendChild(a);
                a.click();
                setTimeout(() => {
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }, 100);
              }}
            >
              here
            </span>
            {' '}to download as csv.
          </>
        )}
      </div>
    </div>
  );
};

export default HRM;
